import React, { useState, useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  FormGroup,
  TabContent,
  TabPane,
  Form,
  InputGroup,
  InputGroupText,
  Input,
  Label,
  Spinner,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import classnames from "classnames"
import apiURl, { baseURl } from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"

const RiderManagement = () => {
  document.title = "ECS Rider Management"
  const navigate = useNavigate()

  // Authorization check
  const role_ = localStorage.getItem("role")
  if (role_ !== "admin") {
    navigate("/")
  }

  // State management
  const [riders, setRiders] = useState([])
  const [modal, setModal] = useState(false)
  const [selectedRider, setSelectedRider] = useState(null)
  const [activeTab, setActiveTab] = useState("2")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  //Rider Modal
  const [riderModal, setRiderModal] = useState(false)
  const [editID, setEditID] = useState(null)

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    cnic: "",
    phone: "",
    cnicFront: null,
    cnicBack: null,
    drivingLicense: null,
    policeCertificate: null,
  })

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFileChange = e => {
    const { name, files } = e.target
    setFormData(prev => ({ ...prev, [name]: files[0] }))
  }

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      cnic: "",
      phone: "",
      cnicFront: null,
      cnicBack: null,
      drivingLicense: null,
      policeCertificate: null,
    })
    setEditID(null)
    setRiderModal(false)
  }

  const [submitLoading, setSubmitLoading] = useState(false)

  const handleSubmitRider = async e => {
    setSubmitLoading(true)
    e.preventDefault()

    const requiredFields = ["name", "password", "cnic", "phone"]
    for (let field of requiredFields) {
      if (!formData[field]) {
        toastr.error(`${field} is required`)
        return
      }
    }
    if (formData.password !== formData.confirmPassword) {
      toastr.error("Passwords do not match")
      return
    }

    try {
      const form = new FormData()
      form.append("name", formData.name)
      form.append("email", formData.email)
      form.append("password", formData.password)
      form.append("cnic", formData.cnic)
      form.append("phone", formData.phone)

      form.append("cnicFront", formData.cnicFront)
      form.append("cnicBack", formData.cnicBack)
      form.append("drivingLicense", formData.drivingLicense)
      form.append("policeCertificate", formData.policeCertificate)

      const res = await axios.post(`${apiURl}/appRegisterRider`, form)

      if (res.data.status === "success") {
        toastr.success("Rider created successfully")
        resetForm()
        getAllRiders()
      } else {
        toastr.error(res.data.message || "Failed to create rider")
      }
    } catch (err) {
      console.error(err)
      toastr.error("Something went wrong during rider creation")
    } finally {
      setSubmitLoading(false)
    }
  }

  //Detail
  const [activeDocTab, setActiveDocTab] = useState("cnicFront")

  const toggleDocTab = tab => {
    if (activeDocTab !== tab) setActiveDocTab(tab)
  }

  const documents = [
    {
      key: "cnicFront",
      label: "CNIC Front",
      url: selectedRider?.cnicFront,
    },
    {
      key: "cnicBack",
      label: "CNIC Back",
      url: selectedRider?.cnicBack,
    },
    {
      key: "drivingLicense",
      label: "Driving License",
      url: selectedRider?.drivingLicense,
    },
    {
      key: "policeCertificate",
      label: "Police Certificate",
      url: selectedRider?.policeCertificate,
    },
  ]

  // Fetch riders data
  const getAllRiders = async () => {
    try {
      setIsLoading(true)
      const URL = `${apiURl}/getAllRiders` // Assuming endpoint is updated
      const response = await axios.get(URL)

      if (response.data.status === "success") {
        setRiders(response.data.riders)
        console.log(response.data.riders)
      } else {
        console.error("Failed to fetch riders:", response.data.message)
      }
    } catch (error) {
      console.error("Error fetching riders:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Update rider status
  const updateRiderStatus = async (riderId, newStatus) => {
    try {
      const URL = `${apiURl}/updateRiderStatus`
      const response = await axios.put(URL, {
        id: riderId,
        status: newStatus,
      })

      if (response.data.status === "success") {
        toastr.success("Status is successfully changed")
        // Refresh riders list
        getAllRiders()
      } else {
        console.error("Failed to update status:", response.data.message)
      }
    } catch (error) {
      console.error("Error updating status:", error)
    }
  }

  // Initialize component
  useEffect(() => {
    getAllRiders()
  }, [])

  // Toggle modal and set selected rider
  const toggleModal = (rider = null) => {
    setSelectedRider(rider)
    setModal(!modal)
  }

  // Toggle between tabs
  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  // Filter riders based on status
  const approvedRiders = riders.filter(rider => rider.status === "Approved")
  const rejectedRiders = riders.filter(rider => rider.status === "Rejected")
  const newRequests = riders.filter(rider => rider.status === "requested")

  // Common columns for both tabs
  const baseColumns = [
    {
      Header: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ value }) => <span>{value ?? "----"}</span>,
    },
    {
      Header: "Name",
      accessor: "name",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
    },
    {
      Header: "Email",
      accessor: "email",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
    },
    {
      Header: "Phone",
      accessor: "phone",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
    },
    {
      Header: "CNIC",
      accessor: "cnic",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "----"}</span>,
    },
    {
      Header: "Created Date",
      accessor: "createdAt",
      disableFilters: true,
      Cell: ({ value }) => <span>{value?.toString().trim() || "-"}</span>,
    },
  ]

  // Columns for Approved Riders
  const approvedColumns = useMemo(
    () => [
      ...baseColumns,
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => (
          <>
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => toggleModal(row.original)}
              >
                View Details
              </button>
              <Link
                className="btn btn-warning btn-sm"
                to={`/rider-wallet/${row.original.id}`}
              >
                <i className="bx bx-dollar"></i> Wallet
              </Link>
            </div>
          </>
        ),
      },
    ],
    []
  )

  // Columns for New Requests (with status change)
  const requestColumns = useMemo(
    () => [
      ...baseColumns,
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        Cell: ({ value, row }) => (
          <select
            value={value}
            onChange={e => updateRiderStatus(row.original.id, e.target.value)}
            className="form-select form-select-sm"
          >
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableFilters: true,
        Cell: ({ row }) => (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => toggleModal(row.original)}
          >
            View Details
          </button>
        ),
      },
    ],
    []
  )

  //
  // put near your state
  const HASH_FOR_TAB = {
    2: "#newRiderReq",
    3: "#rejectedRiderReq",
    1: "#approvedRiderReq",
  }
  const TAB_FOR_HASH = Object.fromEntries(
    Object.entries(HASH_FOR_TAB).map(([k, v]) => [v, k])
  )

  const setHashForTab = tab => {
    const { pathname, search } = window.location
    const hash = HASH_FOR_TAB[tab] || ""
    window.history.replaceState(null, "", `${pathname}${search}${hash}`)
  }

  // on load / back/forward: sync tab from hash
  useEffect(() => {
    const apply = () => {
      const t = TAB_FOR_HASH[window.location.hash]
      if (t && t !== activeTab) toggleTab(t)
    }
    apply()
    window.addEventListener("hashchange", apply)
    return () => window.removeEventListener("hashchange", apply)
  }, [activeTab]) // or [] if toggleTab doesn't depend on activeTab

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Rider Management</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => setRiderModal(true)}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Add New Rider
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  {isLoading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "200px" }}
                    >
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <>
                      <Nav tabs className="d-flex gap-2 flex-wrap">
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "2",
                            })}
                            onClick={() => {
                              toggleTab("2")
                              setHashForTab("2")
                            }}
                            style={{
                              backgroundColor:
                                activeTab === "2" ? "#50A5F1" : "#f8f9fa",
                              color: activeTab === "2" ? "white" : "#6c757d",
                              //   borderRadius: "5px",
                              border: "1px solid #D7DADE",
                              transition: "0.3s",
                            }}
                          >
                            New Requests
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "1",
                            })}
                            onClick={() => {
                              toggleTab("1")
                              setHashForTab("1")
                            }}
                            style={{
                              backgroundColor:
                                activeTab === "1" ? "#34C38F" : "#f8f9fa",
                              color: activeTab === "1" ? "white" : "#6c757d",
                              //   borderRadius: "5px",
                              border: "1px solid #D7DADE",
                              transition: "0.3s",
                            }}
                          >
                            Approved Riders
                          </NavLink>
                        </NavItem>

                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "3",
                            })}
                            onClick={() => {
                              toggleTab("3")
                              setHashForTab("3")
                            }}
                            style={{
                              backgroundColor:
                                activeTab === "3" ? "#F46A6A" : "#f8f9fa",
                              color: activeTab === "3" ? "white" : "#6c757d",
                              //   borderRadius: "5px",
                              border: "1px solid #D7DADE",
                              transition: "0.3s",
                            }}
                          >
                            Rejected Riders
                          </NavLink>
                        </NavItem>
                      </Nav>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                          <TableContainer
                            columns={approvedColumns}
                            data={approvedRiders}
                            isGlobalFilter={true}
                            customPageSize={10}
                            className="custom-table"
                          />
                        </TabPane>
                        <TabPane tabId="2">
                          <TableContainer
                            columns={requestColumns}
                            data={newRequests}
                            isGlobalFilter={true}
                            customPageSize={10}
                            className="custom-table"
                          />
                        </TabPane>
                        <TabPane tabId="3">
                          <TableContainer
                            columns={requestColumns}
                            data={rejectedRiders}
                            isGlobalFilter={true}
                            customPageSize={10}
                            className="custom-table"
                          />
                        </TabPane>
                      </TabContent>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for rider details */}
        <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
          <ModalHeader toggle={toggleModal}>Rider Details</ModalHeader>
          <ModalBody>
            {selectedRider && (
              <div>
                <Row>
                  <Col md="6">
                    <p>
                      <strong>ID:</strong> {selectedRider.id}
                    </p>
                    <p>
                      <strong>Name:</strong> {selectedRider.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedRider.email}
                    </p>

                    <p>
                      <strong>Password:</strong>
                      <InputGroup style={{ maxWidth: "200px" }}>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={selectedRider.password}
                          readOnly
                          className="form-control-sm"
                        />
                        <InputGroupText
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                          />
                        </InputGroupText>
                      </InputGroup>
                    </p>
                  </Col>
                  <Col md="6">
                    <p>
                      <strong>Phone:</strong> {selectedRider.phone}
                    </p>
                    <p>
                      <strong>CNIC:</strong> {selectedRider.cnic}
                    </p>
                    <p>
                      <strong>Status:</strong> {selectedRider.status}
                    </p>
                    <p>
                      <strong>Created Date:</strong> {selectedRider.createdAt}
                    </p>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="12">
                    <h5>
                      <b>Documents</b>
                    </h5>

                    {/* Tabs */}
                    <Nav tabs>
                      {documents.map(doc => (
                        <NavItem key={doc.key}>
                          <NavLink
                            className={classnames({
                              active: activeDocTab === doc.key,
                            })}
                            onClick={() => toggleDocTab(doc.key)}
                            style={{ cursor: "pointer" }}
                          >
                            {doc.label}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>

                    {/* Tab Content */}
                    <TabContent
                      activeTab={activeDocTab}
                      className="p-3 border border-top-0 rounded-bottom"
                    >
                      {documents.map(doc => (
                        <TabPane key={doc.key} tabId={doc.key}>
                          {doc.url ? (
                            <>
                              <div
                                style={{
                                  width: "100%",
                                  maxWidth: "300px",
                                  height: "300px", // ✅ fixed height
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "5px",
                                  backgroundColor: "#f8f9fa",
                                }}
                              >
                                <img
                                  src={`${baseURl}${doc.url}`}
                                  alt={doc.label}
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </div>
                              <div className="d-flex gap-2 mt-2">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() =>
                                    window.open(
                                      `${baseURl}${doc.url}`,
                                      "_blank"
                                    )
                                  }
                                >
                                  View Full
                                </Button>
                              </div>
                            </>
                          ) : (
                            <p className="text-muted">
                              No image available for this document.
                            </p>
                          )}
                        </TabPane>
                      ))}
                    </TabContent>
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          isOpen={riderModal}
          toggle={() => setRiderModal(!riderModal)}
          size="lg"
          centered
        >
          <Form onSubmit={handleSubmitRider}>
            <ModalHeader toggle={() => setRiderModal(!riderModal)}>
              {editID ? "Edit Rider" : "Create New Rider"}
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Full Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>CNIC *</Label>
                    <Input
                      type="text"
                      name="cnic"
                      value={formData.cnic}
                      onChange={handleInputChange}
                      placeholder="Enter CNIC"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>CNIC Front Image *</Label>
                    <Input
                      type="file"
                      name="cnicFront"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                      required={!editID}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Driving License *</Label>
                    <Input
                      type="file"
                      name="drivingLicense"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                      required={!editID}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Phone *</Label>
                    <Input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Confirm Password *</Label>
                    <Input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Re-enter password"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>CNIC Back Image *</Label>
                    <Input
                      type="file"
                      name="cnicBack"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                      required={!editID}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Police Certificate</Label>
                    <Input
                      type="file"
                      name="policeCertificate"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                      required={!editID}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                {editID ? "Update Rider" : "Create Rider"}
              </Button>
            </ModalFooter>
          </Form>
          {submitLoading && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
            >
              <div className="text-center text-white">
                <i
                  className="fas fa-spinner fa-spin"
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                />
              </div>
            </div>
          )}
        </Modal>
      </div>
    </React.Fragment>
  )
}

export default RiderManagement
